import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { login as apiLogin } from '../api/auth.ts';
import { PlatformApiError } from '../api/client.ts';
import { AuthPageFrame } from '../components/AuthPageFrame.tsx';
import { useToast, coerceErrorMessage } from '../components/Toast.tsx';
import { useSession } from '../hooks/useSession.tsx';
import { useT } from '../i18n/index.ts';
import { INPUT_CLASS_ON_LOWEST } from '../styles/forms.ts';

/**
 * Tenant login form. Calls `POST /v1/auth/login` with an email +
 * password pair; relies on the browser to persist the returned session
 * cookie.
 *
 * On success:
 *   - Populates session context with the response shape (accountId,
 *     orgId, email, emailVerified). The email is echoed by platform-api
 *     login responses so the UI can render it immediately without a
 *     second /session round-trip.
 *   - Navigates back to `location.state.from` if present, else
 *     `/dashboard`.
 *
 * On failure: surfaces the platform-api error code + message inline.
 */

/**
 * Narrows the opaque `location.state` blob to a `{ from: string }` shape
 * without an `as`-cast. Returns the path the RequireAuth guard captured,
 * or `null` if the state shape is missing / not a string.
 */
function extractReturnTo(state: unknown): string | null {
  if (state === null || typeof state !== 'object') {
    return null;
  }
  const maybe = Reflect.get(state, 'from');
  if (typeof maybe !== 'string' || maybe.length === 0) {
    return null;
  }
  return maybe;
}

export function Login(): JSX.Element {
  const { status, setUser, fetchImpl } = useSession();
  const toast = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If a logged-in tenant visits /login directly, drop them back into
  // the shell. Avoids a flash of the form during a refresh.
  if (status === 'authenticated') {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    const trimmedEmail = email.trim();
    if (trimmedEmail.length === 0 || password.length === 0) {
      setError(t('login.error.empty_fields'));
      return;
    }
    setSubmitting(true);
    try {
      const response = await apiLogin(
        { email: trimmedEmail, password },
        fetchImpl,
      );
      setUser({
        accountId: response.accountId,
        orgId: response.orgId,
        email: trimmedEmail,
        emailVerified: response.emailVerified,
      });
      const returnTo = extractReturnTo(location.state);
      const target = returnTo !== null ? returnTo : '/dashboard';
      navigate(target, { replace: true });
    } catch (err) {
      const message =
        err instanceof PlatformApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthPageFrame
      eyebrow={t('login.eyebrow')}
      title={t('login.subtitle')}
      description={t('login.description')}
      maxWidthClassName="max-w-[460px]"
      footer={
        <div className="flex flex-col gap-2 text-xs text-outline">
          <Link
            to="/register"
            data-testid="login-register-link"
            className="rounded-lg hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
          >
            {t('login.need_account')}
          </Link>
          <Link
            to="/forgot-password"
            data-testid="login-forgot-link"
            className="rounded-lg hover:text-on-surface focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container"
          >
            {t('login.forgot')}
          </Link>
        </div>
      }
    >
        <form className="flex flex-col gap-6" onSubmit={handleSubmit} noValidate>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('login.email')}
            </span>
            <input
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              disabled={submitting}
              required
              data-testid="login-email"
              className={INPUT_CLASS_ON_LOWEST}
            />
          </label>
          <label className="flex flex-col gap-2">
            <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
              {t('login.password')}
            </span>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              disabled={submitting}
              required
              data-testid="login-password"
              className={INPUT_CLASS_ON_LOWEST}
            />
          </label>
          {error !== null ? (
            <p
              className="m-0 text-xs font-medium text-error"
              role="alert"
              data-testid="login-error"
            >
              {error}
            </p>
          ) : null}
          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit"
            className="mt-2 inline-flex items-center justify-center rounded-xl bg-primary-container px-6 py-3 text-sm font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none"
            >
              {submitting ? t('login.submitting') : t('login.submit')}
            </button>
          </form>
    </AuthPageFrame>
  );
}
